import { useEffect, useMemo, useState } from "react";
import client from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Save, Eye } from "lucide-react";
import { HtmlBlockEditor } from "@/components/HtmlBlockEditor";
import { DealerSearchPicker } from "@/components/DealerSearchPicker";
import { TemplatePreviewDialog } from "@/components/TemplatePreviewDialog";
import { LEGAL_TYPES, legalPlaceholderFields } from "@/lib/i18n";

export default function LegalHtmlEditor() {
  const { t, lang } = useI18n();

  const [businessForm, setBusinessForm] = useState(LEGAL_TYPES[0]);
  const [pendingBusinessForm, setPendingBusinessForm] = useState(null);
  const [template, setTemplate] = useState(null);
  const [html, setHtml] = useState("");
  const [savedHtml, setSavedHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewDealerLabel, setPreviewDealerLabel] = useState("");

  const isDirty = html !== savedHtml;

  const availableFields = useMemo(
    () => legalPlaceholderFields(businessForm, lang),
    [businessForm, lang]
  );

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const { data } = await client.get(`/legal-html-templates/${businessForm}`);
        setTemplate(data);
        const loadedHtml = data?.blocks?.[0]?.html || "";
        setHtml(loadedHtml);
        setSavedHtml(loadedHtml);
      } finally {
        setLoading(false);
      }
    })();
  }, [businessForm]);

  const selectBusinessForm = (form) => {
    if (form === businessForm) return;
    if (isDirty) {
      setPendingBusinessForm(form);
      return;
    }
    setBusinessForm(form);
  };

  const confirmDiscardAndSwitch = () => {
    setBusinessForm(pendingBusinessForm);
    setPendingBusinessForm(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await client.put(`/legal-html-templates/${businessForm}`, { html });
      setTemplate(data);
      const savedNow = data?.blocks?.[0]?.html || "";
      setHtml(savedNow);
      setSavedHtml(savedNow);
      toast.success(t("common.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async (dealer) => {
    setPickerOpen(false);
    setPreviewDealerLabel(`${dealer.name} · ${dealer.global_id}`);
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const { data: summary } = await client.get(`/dealers/${dealer.id}/summary`);
      const fields = {
        ...(summary.dealer.legal_pages || {}),
      };
      const { data } = await client.post("/legal-html-templates/render-preview", {
        business_form: businessForm,
        fields,
      });
      setPreviewHtml(data.html);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("templatePreview.loadFailed"));
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl p-6 md:p-10" data-testid="legal-html-editor-page">
      <div className="mb-6">
        <p className="data-label mb-2">{t("legalHtmlEditor.breadcrumb")}</p>
        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{t("legalHtmlEditor.title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("legalHtmlEditor.subtitle")}</p>
      </div>

      <Card className="p-6" data-testid="legal-html-editor-form">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-xs flex-1">
            <Label className="data-label mb-1 block">{t("legalHtmlEditor.businessForm")}</Label>
            <Select value={businessForm} onValueChange={selectBusinessForm}>
              <SelectTrigger data-testid="legal-html-business-form-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LEGAL_TYPES.map((typ) => (
                  <SelectItem key={typ} value={typ} data-testid={`legal-html-business-form-${typ}`}>
                    {t(`legalTypes.${typ}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="font-mono text-[10px] text-muted-foreground">
            {template?.updated_at
              ? `${t("legalHtmlEditor.lastSaved")}: ${new Date(template.updated_at).toLocaleString(lang)}${template.updated_by_email ? ` · ${template.updated_by_email}` : ""}`
              : t("legalHtmlEditor.neverSaved")}
          </p>
        </div>

        {loading ? (
          <Skeleton className="h-80 w-full" />
        ) : (
          <HtmlBlockEditor
            key={businessForm}
            value={html}
            onChange={setHtml}
            availableFields={availableFields}
            testId="legal-html-block-editor"
          />
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button
            data-testid="legal-html-preview-btn"
            variant="outline"
            onClick={() => setPickerOpen(true)}
            className="gap-1.5"
          >
            <Eye className="h-3.5 w-3.5" />
            {t("legalHtmlEditor.previewForDealer")}
          </Button>
          <Button
            data-testid="legal-html-save-btn"
            onClick={save}
            disabled={saving || !isDirty}
            className="gap-1.5"
          >
            <Save className="h-3.5 w-3.5" />
            {saving ? t("common.loading") : t("common.save")}
          </Button>
        </div>
      </Card>

      <DealerSearchPicker open={pickerOpen} onOpenChange={setPickerOpen} onPick={runPreview} />
      <TemplatePreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        html={previewHtml}
        loading={previewLoading}
        subtitle={previewDealerLabel}
      />

      <AlertDialog open={!!pendingBusinessForm} onOpenChange={(o) => !o && setPendingBusinessForm(null)}>
        <AlertDialogContent data-testid="legal-html-discard-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("legalHtmlEditor.discardTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("legalHtmlEditor.discardDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="legal-html-discard-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="legal-html-discard-confirm-btn" onClick={confirmDiscardAndSwitch}>
              {t("common.yes")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
